import React, { useEffect, useState, useMemo } from "react";
import {
  useFetchHistoryByCollection,
  useDeleteHistoryEntry,
  useFetchHistory,
  useClearHistory,
} from "../../../useQuery";
import { SearchIcon, Trash2Icon, XIcon, TrashIcon, SlidersHorizontalIcon } from "lucide-react";
import { useNotification } from "../../../hooks";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

const METHODS = ["GET", "POST", "PUT", "DELETE", "PATCH"];

const METHOD_STYLES = {
  GET: {
    active: "bg-emerald-100 text-emerald-700 border-emerald-400",
    badge: "bg-emerald-100 text-emerald-700 border border-emerald-300",
  },
  POST: {
    active: "bg-blue-100 text-blue-700 border-blue-400",
    badge: "bg-blue-100 text-blue-700 border border-blue-300",
  },
  PUT: {
    active: "bg-cyan-100 text-cyan-700 border-cyan-400",
    badge: "bg-cyan-100 text-cyan-700 border border-cyan-300",
  },
  DELETE: {
    active: "bg-red-100 text-red-700 border-red-400",
    badge: "bg-red-100 text-red-700 border border-red-300",
  },
  PATCH: {
    active: "bg-violet-100 text-violet-700 border-violet-400",
    badge: "bg-violet-100 text-violet-700 border border-violet-300",
  },
};

const getStatusColor = (code) => {
  if (!code) return "text-zinc-400";
  const s = String(code);
  if (s.startsWith("2")) return "text-emerald-600";
  if (s.startsWith("3")) return "text-blue-600";
  if (s.startsWith("4")) return "text-amber-600";
  if (s.startsWith("5")) return "text-red-600";
  return "text-zinc-500";
};

const parseUrl = (url) => {
  try {
    const u = new URL(url);
    return { host: u.host, path: u.pathname + (u.search || "") };
  } catch {
    return { host: "", path: url };
  }
};

const MethodChip = ({ method, active, onClick }) => {
  const s = METHOD_STYLES[method] || {};
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-[11px] font-bold border tracking-wide transition-all duration-150 cursor-pointer ${
        active
          ? `${s.active} shadow-sm`
          : "bg-white text-zinc-500 border-zinc-300 hover:border-zinc-400 hover:text-zinc-700"
      }`}
    >
      {method}
    </button>
  );
};

// To this:
const HistoryItem = ({ item, onItemClick, onDelete, isDeleting, index, isSelected }) => {
  const req = JSON.parse(item.apiRequest);
  const res = JSON.parse(item.apiResponse);
  const status = res?.status || res?.statusCode;
  const m = req.method?.toUpperCase();
  const { host, path } = parseUrl(req.url);
  const badgeStyle = METHOD_STYLES[m]?.badge || "bg-zinc-100 text-zinc-600 border border-zinc-300";
  const isValidName = (name) =>
    name && name.trim().length > 0 && !["null", "string", "String", "NULL"].includes(name.trim());

  const displayName = isValidName(item.testCaseName) ? item.testCaseName : "Default Endpoint";

  return (
    <div
      onClick={() => onItemClick(req, res, item.testCaseName)}
      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer group border-b border-zinc-200 last:border-b-0 transition-colors duration-100 border-l-2 ${
        isSelected ? "bg-blue-50 border-l-blue-500" : "hover:bg-zinc-100 border-l-transparent"
      }`}
      style={{ animation: `slideIn 0.2s ease-out ${index * 0.03}s backwards` }}
    >
      <span
        className={`shrink-0 font-mono text-[10px] font-bold px-2 py-1 rounded w-[52px] text-center ${badgeStyle}`}
      >
        {m}
      </span>

      <span className="flex-1 flex flex-col min-w-0">
        <span className="text-[11px] text-zinc-500 truncate">{displayName}</span>
        <span className="font-mono text-[12px] truncate">
          <span className="text-zinc-400">{host}</span>
          <span className="text-zinc-700">{path}</span>
        </span>
      </span>

      {status && (
        <span
          className={`shrink-0 font-mono text-[12px] font-semibold w-10 text-right ${getStatusColor(status)}`}
        >
          {status}
        </span>
      )}

      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        disabled={isDeleting}
        className="shrink-0 opacity-0 group-hover:opacity-100 p-1.5 rounded hover:bg-red-100 transition-all duration-150 cursor-pointer"
      >
        <Trash2Icon className="h-3.5 w-3.5 text-zinc-400 group-hover:text-red-500" />
      </button>
    </div>
  );
};

const CollectionGroup = ({ name, items, onItemClick, onDelete, isDeleting, selectedId }) => {
  const [open, setOpen] = useState(true);

  return (
    <div>
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center gap-2.5 px-4 py-2.5 bg-zinc-100 hover:bg-zinc-200 border-b border-zinc-200 transition-colors cursor-pointer"
      >
        <span
          className="text-[10px] text-zinc-500 transition-transform duration-150"
          style={{ transform: open ? "rotate(90deg)" : "none" }}
        >
          ▶
        </span>
        <span className="text-[12px] font-semibold text-zinc-700 truncate flex-1 text-left">
          {name}
        </span>
        <span className="text-[10px] font-bold text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
          {items.length}
        </span>
      </button>
      {open &&
        items.map((item, idx) => (
          <HistoryItem
            key={item.id}
            item={item}
            index={idx}
            onItemClick={onItemClick}
            onDelete={(id) => onDelete(id)}
            isDeleting={isDeleting}
            isSelected={selectedId === item.id}
          />
        ))}
    </div>
  );
};

const ActiveTag = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 text-blue-600 text-[11px] font-semibold rounded-full">
    {label}
    <button onClick={onRemove} className="hover:text-blue-800 transition-colors leading-none">
      ✕
    </button>
  </span>
);

const HistoryPanel = ({ setUrl, setMethod, setResponse5, setActiveTab }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [activeMethods, setActiveMethods] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const notify = useNotification();

  const { data: allHistory, isLoading, refetch } = useFetchHistory({ enabled: !isSearching });
  const { refetch: searchByCollection } = useFetchHistoryByCollection(
    { enabled: false },
    searchQuery,
  );

  const { mutate: deleteEntry, isLoading: isDeleting } = useDeleteHistoryEntry({
    onSuccess: () => {
      notify("Deleted", "success");
      refetch();
    },
    onError: (e) => notify(e.message, "error"),
  });

  const { mutate: clearAll, isLoading: isClearing } = useClearHistory({
    onSuccess: () => {
      notify("History cleared", "success");
      setHistoryData([]);
      setConfirmClear(false);
      setSelectedId(null);
      refetch();
    },
    onError: (e) => {
      notify(e.message, "error");
      setConfirmClear(false);
    },
  });

  useEffect(() => {
    if (allHistory && !isSearching) setHistoryData(allHistory);
  }, [allHistory, isSearching]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      handleClearSearch();
      return;
    }
    setIsSearching(true);
    try {
      const result = await searchByCollection();
      setHistoryData(result.data || []);
    } catch (e) {
      notify(e.message, "error");
      setHistoryData([]);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setIsSearching(false);
    refetch();
  };

  const toggleMethod = (m) => {
    setActiveMethods((prev) => {
      const next = new Set(prev);
      next.has(m) ? next.delete(m) : next.add(m);
      return next;
    });
  };

  const filtered = useMemo(() => {
    if (!Array.isArray(historyData)) return [];
    return historyData.filter((item) => {
      const req = JSON.parse(item.apiRequest);
      const methodOk = activeMethods.size === 0 || activeMethods.has(req.method?.toUpperCase());
      return methodOk;
    });
  }, [historyData, activeMethods]);

  const handleItemClick = (req, res, testCaseName, id) => {
    setSelectedId(id);
    setUrl(req.url);
    setMethod(req.method);
    setResponse5(res);
    setActiveTab("testResults");
  };

  const hasActiveFilters = activeMethods.size > 0 || isSearching;
  const activeFilterCount = activeMethods.size;

  return (
    <div
      className="flex flex-col h-full bg-white select-none"
      style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-3px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Search + Filter Bar */}
      <div className="px-4 pt-4 pb-3 border-b border-zinc-200 bg-zinc-50 space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex flex-1 items-center gap-2 bg-white border border-zinc-300 rounded-lg px-3 focus-within:border-blue-500 transition-colors duration-150">
            <SearchIcon className="h-3.5 w-3.5 text-zinc-400 flex-shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (!e.target.value.trim()) handleClearSearch();
              }}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Search collections…"
              className="flex-1 py-2 text-[13px] bg-transparent border-0 focus:outline-none text-zinc-800 placeholder-zinc-400"
            />
            {(searchQuery || isSearching) && (
              <button onClick={handleClearSearch} className="hover:text-zinc-600 transition-colors">
                <XIcon className="h-3.5 w-3.5 text-zinc-400" />
              </button>
            )}
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-[12px] font-semibold transition-all duration-150 cursor-pointer ${
              showFilters || activeFilterCount > 0
                ? "bg-blue-50 border-blue-400 text-blue-600"
                : "bg-white border-zinc-300 text-zinc-600 hover:border-zinc-400 hover:text-zinc-800"
            }`}
          >
            <SlidersHorizontalIcon className="h-3.5 w-3.5" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-blue-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setConfirmClear(true)}
            disabled={isClearing || filtered.length === 0}
            title="Clear all history"
            className="p-2 rounded-lg border border-zinc-300 text-zinc-400 hover:text-red-500 hover:border-red-300 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 cursor-pointer"
          >
            <TrashIcon className="h-3.5 w-3.5" />
          </button>
        </div>

        {showFilters && (
          <div className="bg-white border border-zinc-200 rounded-lg p-3 space-y-3">
            <div>
              <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                HTTP Method
              </p>
              <div className="flex gap-2 flex-wrap">
                {METHODS.map((m) => (
                  <MethodChip
                    key={m}
                    method={m}
                    active={activeMethods.has(m)}
                    onClick={() => toggleMethod(m)}
                  />
                ))}
              </div>
            </div>
            {activeFilterCount > 0 && (
              <button
                onClick={() => setActiveMethods(new Set())}
                className="text-[11px] text-zinc-500 hover:text-zinc-700 transition-colors"
              >
                Reset filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Active Filter Tags */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 flex-wrap px-4 py-2 bg-zinc-50 border-b border-zinc-200">
          {[...activeMethods].map((m) => (
            <ActiveTag key={m} label={m} onRemove={() => toggleMethod(m)} />
          ))}
          {isSearching && (
            <ActiveTag label={`Collection: ${searchQuery}`} onRemove={handleClearSearch} />
          )}
        </div>
      )}

      {/* Count Bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-zinc-50 border-b border-zinc-200">
        <span className="text-[11px] font-semibold text-zinc-500">
          {filtered.length} {filtered.length === 1 ? "request" : "requests"}
          {hasActiveFilters && <span className="text-blue-500 ml-1.5">(filtered)</span>}
        </span>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-300 scrollbar-track-transparent bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-zinc-400">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
            <span className="text-[12px]">Loading…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-2 text-zinc-400">
            <div className="w-10 h-10 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center mb-1">
              <span className="text-lg">🕐</span>
            </div>
            <p className="text-[13px] font-semibold text-zinc-500">No history found</p>
            {hasActiveFilters && <p className="text-[12px]">Try adjusting your filters</p>}
          </div>
        ) : (
          filtered.map((item, idx) => (
            <HistoryItem
              key={item.id}
              item={item}
              index={idx}
              onItemClick={(req, res, testCaseName) =>
                handleItemClick(req, res, testCaseName, item.id)
              }
              onDelete={(id) => deleteEntry({ historyId: id })}
              isDeleting={isDeleting}
              isSelected={selectedId === item.id}
            />
          ))
        )}
      </div>

      {/* Confirm Clear Dialog */}
      <Dialog
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        PaperProps={{
          style: {
            borderRadius: 10,
            fontFamily: "inherit",
            minWidth: 360,
            background: "#ffffff",
            border: "1px solid #e4e4e7",
            boxShadow: "0 25px 50px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle style={{ fontSize: "1rem", fontWeight: 600, color: "#18181b" }}>
          Clear all history?
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ fontSize: "0.875rem", color: "#71717a", lineHeight: 1.6 }}>
            This will permanently delete all {filtered.length} history{" "}
            {filtered.length === 1 ? "entry" : "entries"}. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "12px 20px", gap: 8 }}>
          <Button
            onClick={() => setConfirmClear(false)}
            style={{
              textTransform: "none",
              fontSize: "0.875rem",
              color: "#71717a",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => clearAll()}
            disabled={isClearing}
            variant="contained"
            style={{
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 600,
              background: "#fee2e2",
              color: "#dc2626",
              boxShadow: "none",
            }}
          >
            {isClearing ? "Clearing…" : "Clear All"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HistoryPanel;
